import type { QueryClient } from "@tanstack/react-query";

import {
  cachePatch,
  cacheRemove,
  cacheSnapshot,
  cacheRestore,
  cacheUpsert,
  invalidateOwned,
  patchRow,
  putRow,
  removeRow,
  type Collection,
  type EntityName,
  type Plant,
  type PlantCareSchedule,
  type PlantJournalEntry,
  type PlantPhoto,
} from "~/state/sync";

/**
 * Every write goes through a mutation *default* registered here rather than an
 * inline `mutationFn`. That is what lets a write made offline be persisted and
 * replayed: `resumePausedMutations()` looks the function up by `mutationKey`.
 *
 * Convention: `mutationKey` is `[entity, op]`, and the variables always carry
 * `userId` so the optimistic cache update (and the rollback) can target
 * `[entity, userId]` without a hook.
 */

type Base = { userId: string };

/** Only a mutation that is the last one in flight restores or refetches, so
 * a failure can't undo a later optimistic change and a burst of quick actions
 * refetches once. `isMutating()` counts this mutation too; mutations paused
 * offline count as pending, which just defers the refetch until they replay. */
const isLastPending = (queryClient: QueryClient) =>
  queryClient.isMutating() <= 1;

/** Entities whose mutation failed while another was still in flight: nothing
 * could be restored safely, so they are refetched when the last one settles. */
const stale = new Set<EntityName>();

/** Shared optimistic wrapper: snapshot the entities the mutation touches,
 * apply `mutate`, roll those back on error, reconcile them on settle. */
function optimistic(
  queryClient: QueryClient,
  touches: readonly EntityName[],
  apply: (userId: string, vars: Record<string, unknown>) => void,
) {
  return {
    onMutate: async (vars: Base & Record<string, unknown>) => {
      // Only this user's entity queries, not every query in the client.
      await queryClient.cancelQueries({
        predicate: (query) => query.queryKey[1] === vars.userId,
      });
      const snapshot = cacheSnapshot(queryClient, vars.userId, touches);
      apply(vars.userId, vars);
      return { snapshot, userId: vars.userId };
    },
    onError: (
      _error: unknown,
      _vars: Base,
      context?: { snapshot: ReturnType<typeof cacheSnapshot>; userId: string },
    ) => {
      if (!context) return;
      if (isLastPending(queryClient)) {
        cacheRestore(queryClient, context.userId, context.snapshot);
      } else {
        touches.forEach((entity) => stale.add(entity));
      }
    },
    onSettled: (_data: unknown, _error: unknown, vars: Base) => {
      if (!isLastPending(queryClient)) return;
      invalidateOwned(queryClient, vars.userId, [
        ...new Set([...touches, ...stale]),
      ]);
      stale.clear();
    },
  };
}

async function insert(entity: EntityName, row: Record<string, unknown>) {
  await putRow(entity, row);
}

async function patch(
  entity: EntityName,
  id: string,
  fields: Record<string, unknown>,
) {
  await patchRow(entity, id, fields);
}

export function installMutationDefaults(queryClient: QueryClient) {
  const set = (
    key: readonly [EntityName, string],
    mutationFn: (vars: never) => Promise<unknown>,
    apply: (userId: string, vars: Record<string, unknown>) => void,
    touches: readonly EntityName[],
  ) => {
    queryClient.setMutationDefaults([...key], {
      mutationFn: mutationFn as (vars: unknown) => Promise<unknown>,
      ...optimistic(queryClient, touches, apply),
    });
  };

  // --- collections -----------------------------------------------------
  set(
    ["collections", "create"] as const,
    async (vars: Base & { row: Collection }) => insert("collections", vars.row),
    (userId, vars) =>
      cacheUpsert(queryClient, "collections", userId, vars.row as Collection),
    ["collections"],
  );
  set(
    ["collections", "update"] as const,
    async (vars: Base & { id: string; fields: Partial<Collection> }) =>
      patch("collections", vars.id, vars.fields),
    (userId, vars) =>
      cachePatch<Collection>(
        queryClient,
        "collections",
        userId,
        vars.id as string,
        vars.fields as Partial<Collection>,
      ),
    ["collections"],
  );
  set(
    ["collections", "remove"] as const,
    async (vars: Base & { id: string }) => {
      // The API trashes the collection's plants (and theirs) along with it.
      await removeRow("collections", vars.id);
    },
    (userId, vars) => {
      cacheRemove<Plant>(
        queryClient,
        "plants",
        userId,
        (plant) => plant.collection_id === vars.id,
      );
      cacheRemove<Collection>(
        queryClient,
        "collections",
        userId,
        (c) => c.id === vars.id,
      );
    },
    ["collections", "plants"],
  );

  // --- plants ---------------------------------------------------------
  set(
    ["plants", "create"] as const,
    async (vars: Base & { row: Plant }) => insert("plants", vars.row),
    (userId, vars) =>
      cacheUpsert(queryClient, "plants", userId, vars.row as Plant),
    ["plants"],
  );
  set(
    ["plants", "update"] as const,
    async (vars: Base & { id: string; fields: Partial<Plant> }) =>
      patch("plants", vars.id, vars.fields),
    (userId, vars) =>
      cachePatch<Plant>(
        queryClient,
        "plants",
        userId,
        vars.id as string,
        vars.fields as Partial<Plant>,
      ),
    ["plants"],
  );
  set(
    ["plants", "remove"] as const,
    async (vars: Base & { id: string }) => {
      // The API trashes the plant's photos, schedules and journal along with it.
      await removeRow("plants", vars.id);
    },
    (userId, vars) => {
      for (const entity of [
        "plant_photos",
        "plant_care_schedules",
        "plant_journal_entries",
      ] as const) {
        cacheRemove<{ id: string; plant_id: string }>(
          queryClient,
          entity,
          userId,
          (row) => row.plant_id === vars.id,
        );
      }
      cacheRemove<Plant>(
        queryClient,
        "plants",
        userId,
        (p) => p.id === vars.id,
      );
    },
    ["plants", "plant_photos", "plant_care_schedules", "plant_journal_entries"],
  );

  // --- plant_photos --------------------------------------------------
  set(
    ["plant_photos", "create"] as const,
    async (vars: Base & { row: PlantPhoto; coverForPlantId?: string }) => {
      await insert("plant_photos", vars.row);
      if (vars.coverForPlantId) {
        await patch("plants", vars.coverForPlantId, {
          cover_photo_path: vars.row.storage_path,
        });
      }
    },
    (userId, vars) => {
      const row = vars.row as PlantPhoto;
      cacheUpsert(queryClient, "plant_photos", userId, row);
      if (vars.coverForPlantId) {
        cachePatch<Plant>(
          queryClient,
          "plants",
          userId,
          vars.coverForPlantId as string,
          {
            cover_photo_path: row.storage_path,
          },
        );
      }
    },
    ["plants", "plant_photos"],
  );
  set(
    ["plant_photos", "remove"] as const,
    async (vars: Base & { id: string }) => removeRow("plant_photos", vars.id),
    (userId, vars) =>
      cacheRemove<PlantPhoto>(
        queryClient,
        "plant_photos",
        userId,
        (photo) => photo.id === vars.id,
      ),
    ["plant_photos"],
  );

  // --- plant_care_schedules ----------------------------------------
  set(
    ["plant_care_schedules", "create"] as const,
    async (vars: Base & { row: PlantCareSchedule }) =>
      insert("plant_care_schedules", vars.row),
    (userId, vars) =>
      cacheUpsert(
        queryClient,
        "plant_care_schedules",
        userId,
        vars.row as PlantCareSchedule,
      ),
    ["plant_care_schedules"],
  );
  set(
    ["plant_care_schedules", "update"] as const,
    async (vars: Base & { id: string; fields: Partial<PlantCareSchedule> }) =>
      patch("plant_care_schedules", vars.id, vars.fields),
    (userId, vars) =>
      cachePatch<PlantCareSchedule>(
        queryClient,
        "plant_care_schedules",
        userId,
        vars.id as string,
        vars.fields as Partial<PlantCareSchedule>,
      ),
    ["plant_care_schedules"],
  );
  set(
    ["plant_care_schedules", "remove"] as const,
    async (vars: Base & { id: string }) =>
      removeRow("plant_care_schedules", vars.id),
    (userId, vars) =>
      cacheRemove<PlantCareSchedule>(
        queryClient,
        "plant_care_schedules",
        userId,
        (schedule) => schedule.id === vars.id,
      ),
    ["plant_care_schedules"],
  );

  // --- plant_journal_entries -------------------------------------
  set(
    ["plant_journal_entries", "create"] as const,
    async (vars: Base & { row: PlantJournalEntry }) =>
      insert("plant_journal_entries", vars.row),
    (userId, vars) =>
      cacheUpsert(
        queryClient,
        "plant_journal_entries",
        userId,
        vars.row as PlantJournalEntry,
      ),
    ["plant_journal_entries"],
  );
  set(
    ["plant_journal_entries", "remove"] as const,
    async (vars: Base & { id: string }) =>
      removeRow("plant_journal_entries", vars.id),
    (userId, vars) =>
      cacheRemove<PlantJournalEntry>(
        queryClient,
        "plant_journal_entries",
        userId,
        (entry) => entry.id === vars.id,
      ),
    ["plant_journal_entries"],
  );
}

/** Stable references for `useMutation({ mutationKey })` in the action hooks. */
export const mk = {
  collectionCreate: ["collections", "create"] as const,
  collectionUpdate: ["collections", "update"] as const,
  collectionRemove: ["collections", "remove"] as const,
  plantCreate: ["plants", "create"] as const,
  plantUpdate: ["plants", "update"] as const,
  plantRemove: ["plants", "remove"] as const,
  photoCreate: ["plant_photos", "create"] as const,
  photoRemove: ["plant_photos", "remove"] as const,
  scheduleCreate: ["plant_care_schedules", "create"] as const,
  scheduleUpdate: ["plant_care_schedules", "update"] as const,
  scheduleRemove: ["plant_care_schedules", "remove"] as const,
  journalCreate: ["plant_journal_entries", "create"] as const,
  journalRemove: ["plant_journal_entries", "remove"] as const,
};

/** Variable shapes for each `mutationKey` — used to type the keyless
 * `useMutation` calls in the action hooks (the `mutationFn` lives in the
 * default, so TS can't infer them). */
export type MutationVars = {
  collectionCreate: Base & { row: Collection };
  collectionUpdate: Base & { id: string; fields: Partial<Collection> };
  collectionRemove: Base & { id: string };
  plantCreate: Base & { row: Plant };
  plantUpdate: Base & { id: string; fields: Partial<Plant> };
  plantRemove: Base & { id: string };
  photoCreate: Base & { row: PlantPhoto; coverForPlantId?: string };
  photoRemove: Base & { id: string };
  scheduleCreate: Base & { row: PlantCareSchedule };
  scheduleUpdate: Base & { id: string; fields: Partial<PlantCareSchedule> };
  scheduleRemove: Base & { id: string };
  journalCreate: Base & { row: PlantJournalEntry };
  journalRemove: Base & { id: string };
};
